import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  FolderOpen, Video, Plus, Trash2, Save, Edit2, ExternalLink, Eye, Star, StarOff,
  Download, RefreshCw, Settings, Youtube, Globe, Image, Film, Palette, TrendingUp,
  Loader2, X, Check, Search, Filter, Grid, List, ChevronDown, Upload
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'web', label: 'Web', icon: Globe, color: 'bg-cyan-100 text-cyan-700' },
  { value: 'video', label: 'Vidéo', icon: Film, color: 'bg-purple-100 text-purple-700' },
  { value: 'graphic', label: 'Graphique', icon: Palette, color: 'bg-pink-100 text-pink-700' },
  { value: 'marketing', label: 'Marketing', icon: TrendingUp, color: 'bg-orange-100 text-orange-700' }
];

const ContentIngestionPage = () => {
  const { token } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('portfolio');
  
  // Data states
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [stats, setStats] = useState(null);
  const [importConfig, setImportConfig] = useState(null);
  
  // UI states
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [importing, setImporting] = useState(false);

  // New project form
  const [newProject, setNewProject] = useState({
    title: '',
    category: 'web',
    client_name: '',
    description: '',
    thumbnail_url: '',
    website_url: '',
    video_url: '',
    tags: [],
    featured: false
  });

  // New video form
  const [newVideo, setNewVideo] = useState({
    video_id: '',
    title: '',
    description: '',
    category: 'general',
    is_featured: false
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [portfolioRes, videosRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/content/portfolio?limit=100`),
        fetch(`${API_URL}/api/content/youtube-videos?limit=100`),
        fetch(`${API_URL}/api/content/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (portfolioRes.ok) {
        const data = await portfolioRes.json();
        setPortfolioProjects(data.projects || []);
      }
      if (videosRes.ok) {
        const data = await videosRes.json();
        setYoutubeVideos(data.videos || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Seed initial content
  const seedContent = async () => {
    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/content/import/seed-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Import failed');

      const result = await response.json();
      toast.success(`Import réussi: ${result.portfolio_imported} projets, ${result.videos_imported} vidéos`);
      fetchData();
    } catch (error) {
      console.error('Error seeding content:', error);
      toast.error('Erreur lors de l\'import du contenu');
    } finally {
      setImporting(false);
    }
  };

  // Create portfolio project
  const createProject = async () => {
    try {
      const response = await fetch(`${API_URL}/api/content/portfolio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newProject)
      });

      if (!response.ok) throw new Error('Creation failed');

      toast.success('Projet créé avec succès');
      setShowAddModal(false);
      setNewProject({
        title: '',
        category: 'web',
        client_name: '',
        description: '',
        thumbnail_url: '',
        website_url: '',
        video_url: '',
        tags: [],
        featured: false
      });
      fetchData();
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Erreur lors de la création');
    }
  };

  // Update portfolio project
  const updateProject = async (projectId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/content/portfolio/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Update failed');

      toast.success('Projet mis à jour');
      setEditingProject(null);
      fetchData();
    } catch (error) {
      console.error('Error updating project:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Delete portfolio project
  const deleteProject = async (projectId) => {
    if (!window.confirm('Supprimer ce projet ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/content/portfolio/${projectId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Delete failed');

      toast.success('Projet supprimé');
      fetchData();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Add YouTube video
  const addYouTubeVideo = async () => {
    if (!newVideo.video_id) {
      toast.error('ID de la vidéo requis');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/content/youtube-videos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newVideo)
      });

      if (!response.ok) throw new Error('Creation failed');

      toast.success('Vidéo ajoutée avec succès');
      setShowVideoModal(false);
      setNewVideo({
        video_id: '',
        title: '',
        description: '',
        category: 'general',
        is_featured: false
      });
      fetchData();
    } catch (error) {
      console.error('Error adding video:', error);
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Delete YouTube video
  const deleteVideo = async (videoId) => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;

    try {
      const response = await fetch(`${API_URL}/api/content/youtube-videos/${videoId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Delete failed');

      toast.success('Vidéo supprimée');
      fetchData();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Toggle featured
  const toggleFeatured = async (projectId, currentFeatured) => {
    await updateProject(projectId, { featured: !currentFeatured });
  };

  // Filter projects
  const filteredProjects = portfolioProjects.filter(project => {
    const matchesSearch = project.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.client_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || project.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Filter videos
  const filteredVideos = youtubeVideos.filter(video =>
    video.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get category badge
  const getCategoryBadge = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    if (!cat) return null;
    const IconComponent = cat.icon;
    return (
      <Badge className={cn("flex items-center gap-1", cat.color)}>
        <IconComponent className="w-3 h-3" />
        {cat.label}
      </Badge>
    );
  };

  // Extract YouTube video ID from URL
  const extractVideoId = (url) => {
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : url;
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <div className={`flex-1 transition-all duration-300 ${collapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <Header setMobileOpen={setMobileOpen} />

        <main className="p-4 md:p-6 pt-24">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion du Contenu</h1>
              <p className="text-gray-500">Portfolio et vidéos YouTube importés</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={seedContent}
                disabled={importing}
                variant="outline"
                className="gap-2"
              >
                {importing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Importer depuis agence-msc.fr
              </Button>
              <Button onClick={fetchData} variant="outline" size="icon">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FolderOpen className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.portfolio?.total || 0}</p>
                      <p className="text-xs text-gray-500">Projets Portfolio</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <Youtube className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.videos?.total || 0}</p>
                      <p className="text-xs text-gray-500">Vidéos YouTube</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Star className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{stats.portfolio?.featured || 0}</p>
                      <p className="text-xs text-gray-500">Projets Mis en Avant</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">
                        {stats.import_config?.auto_import_enabled ? 'Actif' : 'Inactif'}
                      </p>
                      <p className="text-xs text-gray-500">Import Auto</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <TabsList>
                <TabsTrigger value="portfolio" className="gap-2">
                  <FolderOpen className="w-4 h-4" />
                  Portfolio ({filteredProjects.length})
                </TabsTrigger>
                <TabsTrigger value="youtube" className="gap-2">
                  <Youtube className="w-4 h-4" />
                  Vidéos ({filteredVideos.length})
                </TabsTrigger>
              </TabsList>

              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-48"
                  />
                </div>
                {activeTab === 'portfolio' && (
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex border rounded-lg">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('grid')}
                    className="rounded-r-none"
                  >
                    <Grid className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="icon"
                    onClick={() => setViewMode('list')}
                    className="rounded-l-none"
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Portfolio Tab */}
            <TabsContent value="portfolio">
              <div className="flex justify-end mb-4">
                <Button onClick={() => setShowAddModal(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Ajouter un projet
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : filteredProjects.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <FolderOpen className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 mb-4">Aucun projet dans le portfolio</p>
                    <Button onClick={seedContent} variant="outline" className="gap-2">
                      <Download className="w-4 h-4" />
                      Importer le contenu initial
                    </Button>
                  </CardContent>
                </Card>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredProjects.map(project => (
                    <Card key={project.id} className="overflow-hidden group">
                      <div className="relative aspect-video bg-gray-100">
                        {project.thumbnail_url ? (
                          <img
                            src={project.thumbnail_url}
                            alt={project.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <Image className="w-12 h-12 text-gray-300" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setEditingProject(project)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => toggleFeatured(project.id, project.featured)}
                          >
                            {project.featured ? (
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            ) : (
                              <StarOff className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteProject(project.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {project.featured && (
                          <Badge className="absolute top-2 right-2 bg-yellow-400 text-yellow-900">
                            <Star className="w-3 h-3 mr-1" />
                            Featured
                          </Badge>
                        )}
                      </div>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900 line-clamp-1">{project.title}</h3>
                          {getCategoryBadge(project.category)}
                        </div>
                        {project.client_name && (
                          <p className="text-sm text-gray-500 mb-2">Client: {project.client_name}</p>
                        )}
                        <p className="text-sm text-gray-600 line-clamp-2">{project.description}</p>
                        {project.is_imported && (
                          <Badge variant="outline" className="mt-2 text-xs">
                            Importé de {project.import_source}
                          </Badge>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <div className="divide-y">
                    {filteredProjects.map(project => (
                      <div key={project.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                        <div className="w-20 h-14 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                          {project.thumbnail_url ? (
                            <img
                              src={project.thumbnail_url}
                              alt={project.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full">
                              <Image className="w-6 h-6 text-gray-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900">{project.title}</h3>
                            {project.featured && (
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{project.client_name || 'Pas de client'}</p>
                        </div>
                        {getCategoryBadge(project.category)}
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingProject(project)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteProject(project.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </TabsContent>

            {/* YouTube Tab */}
            <TabsContent value="youtube">
              <div className="flex justify-end mb-4">
                <Button onClick={() => setShowVideoModal(true)} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Ajouter une vidéo
                </Button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                </div>
              ) : filteredVideos.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Youtube className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 mb-4">Aucune vidéo importée</p>
                    <Button onClick={seedContent} variant="outline" className="gap-2">
                      <Download className="w-4 h-4" />
                      Importer le contenu initial
                    </Button>
                  </CardContent>
                </Card>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredVideos.map(video => (
                    <Card key={video.id} className="overflow-hidden group">
                      <div className="relative aspect-video bg-gray-900">
                        <img
                          src={video.thumbnail_url || `https://img.youtube.com/vi/${video.video_id}/maxresdefault.jpg`}
                          alt={video.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = `https://img.youtube.com/vi/${video.video_id}/hqdefault.jpg`;
                          }}
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                            <Video className="w-8 h-8 text-white ml-1" />
                          </div>
                        </div>
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => window.open(video.watch_url, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deleteVideo(video.video_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {video.is_featured && (
                          <Badge className="absolute top-2 right-2 bg-red-600 text-white">
                            <Star className="w-3 h-3 mr-1" />
                            Featured
                          </Badge>
                        )}
                      </div>
                      <CardContent className="pt-4">
                        <h3 className="font-semibold text-gray-900 line-clamp-2 mb-1">{video.title}</h3>
                        <p className="text-sm text-gray-500">{video.channel_name}</p>
                        <p className="text-xs text-gray-400 mt-1">ID: {video.video_id}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <div className="divide-y">
                    {filteredVideos.map(video => (
                      <div key={video.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                        <div className="w-28 h-16 bg-gray-900 rounded overflow-hidden flex-shrink-0 relative">
                          <img
                            src={video.thumbnail_url || `https://img.youtube.com/vi/${video.video_id}/mqdefault.jpg`}
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
                              <Video className="w-4 h-4 text-white" />
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 line-clamp-1">{video.title}</h3>
                          <p className="text-sm text-gray-500">{video.channel_name}</p>
                        </div>
                        {video.is_featured && (
                          <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        )}
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(video.watch_url, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteVideo(video.video_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </main>
      </div>

      {/* Add Project Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Nouveau Projet Portfolio
                <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titre *</Label>
                <Input
                  value={newProject.title}
                  onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                  placeholder="Nom du projet"
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select
                  value={newProject.category}
                  onValueChange={(val) => setNewProject({ ...newProject, category: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client</Label>
                <Input
                  value={newProject.client_name}
                  onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })}
                  placeholder="Nom du client"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  placeholder="Description du projet"
                  rows={3}
                />
              </div>
              <div>
                <Label>URL de l'image</Label>
                <Input
                  value={newProject.thumbnail_url}
                  onChange={(e) => setNewProject({ ...newProject, thumbnail_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>URL du site web</Label>
                <Input
                  value={newProject.website_url}
                  onChange={(e) => setNewProject({ ...newProject, website_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newProject.featured}
                  onCheckedChange={(checked) => setNewProject({ ...newProject, featured: checked })}
                />
                <Label>Mettre en avant</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAddModal(false)}>
                  Annuler
                </Button>
                <Button onClick={createProject} disabled={!newProject.title}>
                  <Save className="w-4 h-4 mr-2" />
                  Créer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Ajouter une Vidéo YouTube
                <Button variant="ghost" size="icon" onClick={() => setShowVideoModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>URL ou ID de la vidéo *</Label>
                <Input
                  value={newVideo.video_id}
                  onChange={(e) => {
                    const extracted = extractVideoId(e.target.value);
                    setNewVideo({ ...newVideo, video_id: extracted });
                  }}
                  placeholder="https://youtube.com/watch?v=... ou ID"
                />
                <p className="text-xs text-gray-500 mt-1">
                  ID détecté: {newVideo.video_id || 'N/A'}
                </p>
              </div>
              <div>
                <Label>Titre</Label>
                <Input
                  value={newVideo.title}
                  onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                  placeholder="Titre de la vidéo"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={newVideo.description}
                  onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                  placeholder="Description"
                  rows={3}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newVideo.is_featured}
                  onCheckedChange={(checked) => setNewVideo({ ...newVideo, is_featured: checked })}
                />
                <Label>Mettre en avant</Label>
              </div>
              {newVideo.video_id && (
                <div className="aspect-video bg-gray-900 rounded overflow-hidden">
                  <img
                    src={`https://img.youtube.com/vi/${newVideo.video_id}/maxresdefault.jpg`}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = `https://img.youtube.com/vi/${newVideo.video_id}/hqdefault.jpg`;
                    }}
                  />
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowVideoModal(false)}>
                  Annuler
                </Button>
                <Button onClick={addYouTubeVideo} disabled={!newVideo.video_id}>
                  <Youtube className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Project Modal */}
      {editingProject && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Modifier le Projet
                <Button variant="ghost" size="icon" onClick={() => setEditingProject(null)}>
                  <X className="w-5 h-5" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titre</Label>
                <Input
                  value={editingProject.title}
                  onChange={(e) => setEditingProject({ ...editingProject, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select
                  value={editingProject.category}
                  onValueChange={(val) => setEditingProject({ ...editingProject, category: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client</Label>
                <Input
                  value={editingProject.client_name}
                  onChange={(e) => setEditingProject({ ...editingProject, client_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingProject.description}
                  onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <Label>URL de l'image</Label>
                <Input
                  value={editingProject.thumbnail_url}
                  onChange={(e) => setEditingProject({ ...editingProject, thumbnail_url: e.target.value })}
                />
              </div>
              <div>
                <Label>URL du site web</Label>
                <Input
                  value={editingProject.website_url}
                  onChange={(e) => setEditingProject({ ...editingProject, website_url: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editingProject.featured}
                  onCheckedChange={(checked) => setEditingProject({ ...editingProject, featured: checked })}
                />
                <Label>Mettre en avant</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingProject(null)}>
                  Annuler
                </Button>
                <Button onClick={() => updateProject(editingProject.id, editingProject)}>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ContentIngestionPage;
